import React, { useMemo } from "react";
import { Map, MapMarker } from "reaviz";
import { feature } from "topojson-client";
import geojson from "world-atlas/countries-110m.json";

const worldData = feature(geojson, geojson.objects.countries);

// Helper function to normalize text for better searching (same as HomePage)
const normalizeSearchText = (text) => {
  return text
    .toLowerCase()
    .replace(/[.,\-_\s]+/g, "") // Remove dots, commas, dashes, underscores, and spaces
    .replace(/[^\w]/g, ""); // Remove any remaining non-word characters
};

// Custom styling for markers
const getMarkerStyle = (location, searchTerm) => {
  // Only show markers when there's a search term
  if (!searchTerm) {
    return null;
  }

  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  const normalizedTitle = normalizeSearchText(location.title);

  const isMatching = normalizedTitle.includes(normalizedSearchTerm);

  // Only show matching locations
  if (!isMatching) {
    return null;
  }

  return {
    fill: "#e53e3e",
    size: 3,
    strokeWidth: 0,
    stroke: "none",
    opacity: 1,
    style: {
      fill: "#e53e3e !important", // Force red color with !important
      opacity: "1 !important",
    },
    className: "location-marker matching",
  };
};

const WorldMap = ({ locations = [], onLocationClick, searchTerm = "" }) => {
  // Memoize the markers to prevent unnecessary re-renders
  const markers = useMemo(() => {
    // If no search term, show no markers
    if (!searchTerm) {
      return [];
    }

    return locations
      .map((location) => {
        const style = getMarkerStyle(location, searchTerm);
        if (!style) return null;
        const coordinates = location.coordinates || [0, 0];
        return (
          <MapMarker
            key={location.id || location.url}
            coordinates={coordinates}
            tooltip={location.title}
            onClick={() => onLocationClick && onLocationClick(location)}
            fill={style.fill}
            size={style.size}
            strokeWidth={style.strokeWidth}
            stroke={style.stroke}
            className={style.className}
            style={style.style}
          />
        );
      })
      .filter(Boolean);
  }, [locations, searchTerm, onLocationClick]);

  return (
    <div className="w-full h-full" style={{ minHeight: "600px" }}>
      <Map
        data={worldData}
        height="100%"
        width="100%"
        fill="#f7f7f7"
        stroke="#e2e2e2"
        strokeWidth={0.5}
        graticule={true}
        graticuleStroke="#e2e2e2"
        graticuleStrokeWidth={0.2}
        markers={markers}
      />
    </div>
  );
};

export default React.memo(WorldMap);
